/**
 * TodaysBrief.tsx — /todays-brief
 *
 * Static intelligence update page. Shows what is live, in progress, and coming.
 * No backend — seeded from a TypeScript array.
 *
 * Mi6 palette: Navy #0B0B0F · Red #DC2626 · background #FAFAF7
 */

import DualGoalProgressBar from "@/components/DualGoalProgressBar";
import OperatorShell from "@/components/operator/OperatorShell";
import { useSprintConfig } from "@/lib/useSprintConfig";
import { useMyGoals } from "@/lib/useMyGoals";

const MI6_BLACK  = "#0B0B0F";
const MI6_RED   = "#DC2626";
const MI6_BG    = "#FAFAF7";

// ─── Entry types ──────────────────────────────────────────────────────────────

type EntryStatus = "live" | "in_progress" | "coming";
type EntrySection = "coming" | "today" | "week" | "recent";

interface BriefEntry {
  id: string;
  status: EntryStatus;
  title: string;
  description: string;
  route?: string;
  section: EntrySection;
}

// ─── Seed data ────────────────────────────────────────────────────────────────

const ENTRIES: BriefEntry[] = [
  // ── COMING SOON ──
  {
    id: "coming_helix_3d",
    status: "coming",
    title: "3D Helix score visualization",
    description: "Every Checkpoint rendered as a colored orb — cleared, partial, open — in a spatial Helix layout.",
    section: "coming",
  },
  {
    id: "coming_gmail_oauth",
    status: "coming",
    title: "Send-from-system (Gmail / Outlook OAuth)",
    description: "One-click send directly from the compose panel via connected Gmail or Outlook account.",
    section: "coming",
  },
  {
    id: "coming_dashboard_picker",
    status: "coming",
    title: "Global dashboard picker",
    description: "Set your default role view — AE, Management, Executive — and jump straight in on each session.",
    section: "coming",
  },
  {
    id: "coming_override_accuracy",
    status: "coming",
    title: "Override accuracy report per ae",
    description: "Per-AE override resolution history — closed on date, slipped, lost — exported for pipeline reviews.",
    section: "coming",
  },

  // ── TODAY ──
  {
    id: "today_helix_v2",
    status: "live",
    title: "10 Checkpoints — Helix Model v2",
    description: "Upgraded from 9 to 10 checkpoints, weighted by tier. Tier 1 (60 pts) drives qualification. Tier 2 (40 pts) surfaces closing blockers.",
    route: "/#/reporting/account-detail",
    section: "today",
  },
  {
    id: "today_pursuit_decision",
    status: "live",
    title: "Pursuit Decision",
    description: "Clearer name for the end-of-cadence outcome at Step 7. Replaces the previous label. Three outcomes: Marketing Hook, Park 90 Days, Disqualify.",
    route: "/#/cadence",
    section: "today",
  },
  {
    id: "today_forecast_override",
    status: "live",
    title: "Forecast Override",
    description: "Manager-controlled, single-reason, audit-tracked. Three modes: Off, Rep-allowed, Manager-only. Every override carries a close-date commitment and justification.",
    route: "/#/settings/override-policy",
    section: "today",
  },
  {
    id: "today_trust_index",
    status: "live",
    title: "Forecast Trust Index",
    description: "System score commits vs override commits shown side-by-side on the Reporting Hub. Per-AE override accuracy tracked and resolved.",
    route: "/#/reporting",
    section: "today",
  },
  {
    id: "today_reporting_hub",
    status: "live",
    title: "Reporting Hub",
    description: "Five management deep-views: Team Scoreboard, Accounts in Flight, Source Funnel, Data Hygiene, AE Detail. All client-side, no backend dependency.",
    route: "/#/reporting",
    section: "today",
  },
  {
    id: "today_doc_library",
    status: "live",
    title: "Document Library",
    description: "293 scripts, objections, discovery questions, and battle cards. Organized by phase and category. Search and filter built in.",
    route: "/#/library",
    section: "today",
  },
  {
    id: "today_cadence_builder",
    status: "live",
    title: "Cadence Builder",
    description: "Configurable up to 10 steps. Manager sets action type, day offset, and script per step. Default Mi6 Standard Cadence ships locked and ready.",
    route: "/#/cadence/builder",
    section: "today",
  },

  // ── THIS WEEK ──
  {
    id: "week_cadence_engine",
    status: "live",
    title: "BDR + AE Cadence Engine",
    description: "7-step program with daily queue. Prospects enrolled, steps completed, Pursuit Decision resolved — all tracked in localStorage.",
    route: "/#/cadence",
    section: "week",
  },
  {
    id: "week_completion_math",
    status: "live",
    title: "X of Y completion math on every queue",
    description: "Step counters on the cadence queue show done vs due per step, with compliance percentage calculated live.",
    route: "/#/cadence",
    section: "week",
  },
  {
    id: "week_marketing_views",
    status: "live",
    title: "Marketing Plan / Calendar / Hooks views",
    description: "Three views built for the Marketing role: Marketing Plan with event tracking, Campaign Calendar, and Hook Adoption report.",
    route: "/#/marketing/plan",
    section: "week",
  },
  {
    id: "week_activity_log",
    status: "live",
    title: "Activity log formatting cleanup",
    description: "BDR and AE daily activity logs cleaned up: consistent field labels, proper date formatting, mobile layout fixes.",
    route: "/#/log/bdr",
    section: "week",
  },

  // ── RECENT ──
  {
    id: "recent_cadence_health_tile",
    status: "live",
    title: "Cadence Health Tile on every dashboard",
    description: "Real-time cadence compliance tile on AE, Manager, Executive, and BDR dashboards. Shows due, done, and overdue counts.",
    route: "/#/dashboards-preview",
    section: "recent",
  },
  {
    id: "recent_compose_send",
    status: "live",
    title: "Compose and Send Tier 1",
    description: "Mailto launch, tel: dial, and clipboard copy from the cadence queue compose panel. No OAuth required for Tier 1.",
    route: "/#/cadence",
    section: "recent",
  },
  {
    id: "recent_email_scaffold",
    status: "in_progress",
    title: "Email integration scaffold (Gmail / Outlook)",
    description: "Settings page and token storage scaffolded. OAuth flow is next. Send-from-system ships once OAuth is wired.",
    route: "/#/settings/email",
    section: "recent",
  },
  {
    id: "recent_rep_reporting",
    status: "live",
    title: "Per-AE / per-account / per-source reporting",
    description: "Rep Detail drill-down, Accounts in Flight with all filters, and Source Funnel by prospect origin. Team Scoreboard with sort and filter.",
    route: "/#/reporting",
    section: "recent",
  },
];

// ─── Section config ───────────────────────────────────────────────────────────

const SECTIONS: {
  key: EntrySection;
  label: string;
  accentColor: string;
  accentBg: string;
  description: string;
}[] = [
  {
    key: "coming",
    label: "Coming Soon",
    accentColor: MI6_BLACK,
    accentBg: `${MI6_BLACK}08`,
    description: "Features under active development — not yet live.",
  },
  {
    key: "today",
    label: "Today",
    accentColor: "#16A34A",
    accentBg: "#DCFCE7",
    description: "Shipped in this deployment.",
  },
  {
    key: "week",
    label: "This Week",
    accentColor: MI6_BLACK,
    accentBg: `${MI6_BLACK}08`,
    description: "Shipped in the last seven days.",
  },
  {
    key: "recent",
    label: "Recent",
    accentColor: "#6B6B70",
    accentBg: "#EFEFEE",
    description: "Shipped in the last 30 days.",
  },
];

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: EntryStatus }) {
  if (status === "live") {
    return (
      <span
        title="Live"
        style={{
          display: "inline-block",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: "#16A34A",
          flexShrink: 0,
        }}
      />
    );
  }
  if (status === "in_progress") {
    return (
      <span
        title="In progress"
        style={{
          display: "inline-block",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: "#CA8A04",
          flexShrink: 0,
        }}
      />
    );
  }
  // coming
  return (
    <span
      title="Coming soon"
      style={{
        display: "inline-block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        border: `2px solid ${MI6_BLACK}`,
        background: "transparent",
        flexShrink: 0,
      }}
    />
  );
}

function StatusLabel({ status }: { status: EntryStatus }) {
  const configs = {
    live:        { label: "Live",        color: "#16A34A", bg: "#DCFCE780" },
    in_progress: { label: "In progress", color: "#CA8A04", bg: "#FEF9C380" },
    coming:      { label: "Coming",      color: MI6_BLACK,  bg: `${MI6_BLACK}10` },
  };
  const cfg = configs[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "99px",
        background: cfg.bg,
        fontSize: "9px",
        fontWeight: 800,
        color: cfg.color,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <StatusDot status={status} />
      {cfg.label}
    </span>
  );
}

// ─── Entry row ────────────────────────────────────────────────────────────────

function EntryRow({ entry }: { entry: BriefEntry }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 0",
        borderTop: "1px solid #EFEFEE",
      }}
    >
      {/* Status */}
      <div style={{ paddingTop: "3px", flexShrink: 0 }}>
        <StatusLabel status={entry.status} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "13px", fontWeight: 700, color: MI6_BLACK, lineHeight: 1.3 }}>
          {entry.title}
        </div>
        <div style={{ fontSize: "12px", color: "#6B6B70", marginTop: "3px", lineHeight: 1.5 }}>
          {entry.description}
        </div>
      </div>

      {/* Link */}
      {entry.route && (
        <a
          href={entry.route}
          style={{
            flexShrink: 0,
            padding: "5px 12px",
            background: `${MI6_BLACK}0a`,
            border: `1px solid ${MI6_BLACK}18`,
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: 700,
            color: MI6_BLACK,
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${MI6_BLACK}16`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `${MI6_BLACK}0a`;
          }}
        >
          Open →
        </a>
      )}
    </div>
  );
}

// ─── Section block ────────────────────────────────────────────────────────────

function SectionBlock({
  section,
}: {
  section: typeof SECTIONS[number];
}) {
  const entries = ENTRIES.filter((e) => e.section === section.key);
  if (entries.length === 0) return null;

  return (
    <div
      data-tour="todays-brief"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        marginBottom: "20px",
      }}
    >
      {/* Section header */}
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #EFEFEE",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          background: section.accentBg,
        }}
      >
        <div
          style={{
            width: "4px",
            height: "24px",
            borderRadius: "2px",
            background: section.accentColor,
            flexShrink: 0,
          }}
        />
        <div>
          <div style={{ fontSize: "15px", fontWeight: 800, color: MI6_BLACK }}>
            {section.label}
          </div>
          <div style={{ fontSize: "11px", color: "#6B6B70", marginTop: "1px" }}>
            {section.description}
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            padding: "3px 10px",
            background: `${section.accentColor}14`,
            borderRadius: "99px",
            fontSize: "10px",
            fontWeight: 700,
            color: section.accentColor,
          }}
        >
          {entries.length} {entries.length === 1 ? "item" : "items"}
        </div>
      </div>

      {/* Entry rows */}
      <div style={{ padding: "0 24px" }}>
        {entries.map((entry) => (
          <EntryRow key={entry.id} entry={entry} />
        ))}
        <div style={{ height: "8px" }} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function SprintHeadline() {
  const { window: w } = useSprintConfig();
  const { goals } = useMyGoals();
  if (!w) return null;
  const remaining = Math.max(0, goals.sprintGoal - goals.sprintActual - goals.sprintCommitted);
  const fmt = (n: number) => n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;
  return (
    <div style={{
      marginBottom: 24,
      padding: "16px 20px",
      background: "#FFFFFF",
      border: "1px solid #E5E5E7",
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
    }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#DC2626", letterSpacing: "0.16em", textTransform: "uppercase" }}>
          Accelerated Jumpstart
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0B0B0F", marginTop: 4 }}>
          Jumpstart gap: {fmt(remaining)} to goal
        </div>
      </div>
      <div style={{ flex: "0 0 320px" }}>
        <DualGoalProgressBar />
      </div>
    </div>
  );
}

export default function TodaysBrief() {
  const liveCount  = ENTRIES.filter((e) => e.status === "live").length;
  const inProgCount = ENTRIES.filter((e) => e.status === "in_progress").length;
  const comingCount = ENTRIES.filter((e) => e.status === "coming").length;

  return (
    <OperatorShell breadcrumbs={[{ label: "Today's Brief" }]} pageTitle="Today's Brief" pageMeta="Intelligence updates">

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "40px 24px" }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: "20px" }}>
          <a
            href="/dashboards-preview"
            style={{ fontSize: "12px", color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
          >
            ← Dashboards
          </a>
        </div>

        <SprintHeadline />

        {/* Title */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 900,
              color: MI6_BLACK,
              margin: "0 0 6px",
              letterSpacing: "-0.01em",
            }}
          >
            Mi6 Morning Brief
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#6B6B70",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Intelligence updates &nbsp;&middot;&nbsp; what's live, what's next
          </p>
        </div>

        {/* Summary bar */}
        <div
          style={{
            background: MI6_BLACK,
            borderRadius: "12px",
            padding: "18px 26px",
            marginBottom: "28px",
            display: "flex",
            gap: "28px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {[
            { label: "Live",        value: liveCount,   color: "#86EFAC" },
            { label: "In Progress", value: inProgCount, color: "#FDE68A" },
            { label: "Coming Soon", value: comingCount, color: "#C9C9CC" },
            { label: "Total",       value: ENTRIES.length, color: "#FAFAF7" },
          ].map((s) => (
            <div key={s.label}>
              <div
                style={{
                  fontSize: "9px",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#525258",
                  marginBottom: "3px",
                }}
              >
                {s.label}
              </div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 900,
                  color: s.color,
                  lineHeight: 1,
                }}
              >
                {s.value}
              </div>
            </div>
          ))}

          <div style={{ marginLeft: "auto" }}>
            <div
              style={{
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#525258",
                marginBottom: "3px",
              }}
            >
              As of
            </div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#8E8E94" }}>
              May 6, 2026
            </div>
          </div>
        </div>

        {/* Sections — in display order: coming, today, week, recent */}
        {SECTIONS.map((section) => (
          <SectionBlock key={section.key} section={section} />
        ))}

        {/* Legend */}
        <div
          style={{
            marginTop: "24px",
            padding: "14px 20px",
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: "10px",
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#8E8E94",
              flexShrink: 0,
            }}
          >
            Key
          </span>
          {[
            { status: "live"        as EntryStatus, desc: "Deployed and accessible in this build" },
            { status: "in_progress" as EntryStatus, desc: "Scaffolded, not yet fully functional" },
            { status: "coming"      as EntryStatus, desc: "Scoped, not yet started" },
          ].map(({ status, desc }) => (
            <div key={status} style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <StatusLabel status={status} />
              <span style={{ fontSize: "11px", color: "#6B6B70" }}>{desc}</span>
            </div>
          ))}
        </div>

      </div>

    </OperatorShell>
  );
}
